import styled, { css } from 'styled-components';
import { Box, Divider, Heading, Stack, SubtleText } from '@99designs/emu';
import format from '@99designs/ninedown/dist/Format';
import sanitize from '@99designs/ninedown/dist/Sanitize';
import { linkTo } from '@99designs/routing';
import { useTrackLink } from '@99designs/tracking';
import { AdditionalCost } from '../AdditionalCost';
import { BaseLayout } from '../BaseLayout';
import { CheckoutSummary } from '../CheckoutSummary';
import { CollaborationMethod, collaborationMethods } from '../CollaborationMethod';
import { Hero } from '../Hero';
import { HowItWorks, howItWorksContent } from '../HowItWorks';
import { PackageInclusions } from '../PackageInclusions';
import { ProductGroupSummary } from '../ProductGroupSummary';
import type { ZendeskLanguage } from '@99designs/routing';
import type {
    FixedPriceProjectProduct,
    ProductGroup,
    QuestionAndAnswer,
    RelatedProduct,
} from '../../types';
import type { InspirationProps } from '../BaseLayout/BaseLayout';

const InclusionsWrapper = styled(Box)<{ lastItem: boolean }>`
    ${({ lastItem }) =>
        lastItem &&
        css`
            padding-bottom: 0;
        `}
`;

export type Props = {
    productGroup: ProductGroup;
    products: FixedPriceProjectProduct[];
    inspiration?: InspirationProps;
    relatedProducts?: RelatedProduct[];
    faqs: QuestionAndAnswer[];
    lang: ZendeskLanguage;
};

export function FixedPriceProjectLayout({
    productGroup,
    products,
    inspiration,
    relatedProducts,
    faqs,
    lang,
}: Props) {
    const product = products[0];

    const continueCTARef = useTrackLink(
        {
            event: 'Continue CTA on Product details page clicked',
            meta: {},
        },
        []
    );

    return (
        <BaseLayout
            relatedProducts={relatedProducts}
            inspiration={inspiration}
            faqs={
                faqs.length > 0
                    ? { title: productGroup.attributes.title || '', body: faqs }
                    : undefined
            }
            leftColumn={
                <>
                    <Hero
                        images={productGroup.attributes.hero}
                        type={productGroup.attributes.type}
                    />
                    <Box
                        display={['none', 'block', 'block', 'block']}
                        paddingTop={['none', '1.5bu', '1.5bu', '1.5bu']}
                    >
                        <HowItWorks
                            {...howItWorksContent(productGroup.attributes.collaborationMethod)}
                        />
                    </Box>
                </>
            }
            rightColumn={
                <>
                    <ProductGroupSummary
                        title={product.title}
                        price={product.pricing}
                        subtitle={productGroup.attributes.subtitle}
                        description={product.description}
                        type={productGroup.attributes.type}
                    />
                    <Divider />
                    <PackageInclusions
                        inclusions={product.details.map((detail, i) => {
                            const isLastItem = i + 1 === product.details.length;
                            return (
                                <InclusionsWrapper
                                    paddingBottom={['1bu', '1bu', '1.5bu', '1.5bu']}
                                    lastItem={isLastItem}
                                >
                                    <Stack space="0.25bu">
                                        <Heading appearanceLevel={5} accessibilityLevel={7} as="p">
                                            {detail.title}
                                        </Heading>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: format(sanitize(detail.description)),
                                            }}
                                        />
                                    </Stack>
                                </InclusionsWrapper>
                            );
                        })}
                    />

                    <CollaborationMethod
                        type={productGroup.attributes.collaborationMethod}
                        {...collaborationMethods(productGroup.attributes.collaborationMethod, lang)}
                    />

                    {product.additionalDetails.length > 0 && (
                        <AdditionalCost items={product.additionalDetails} />
                    )}

                    {product.disclaimer && (
                        <SubtleText>
                            {__('Disclaimer')}: {product.disclaimer}
                        </SubtleText>
                    )}

                    <CheckoutSummary
                        pricing={product.pricing}
                        cta={{
                            key: product.name,
                            ref: continueCTARef,
                            href: linkTo.briefCreate(product.name),
                        }}
                    />
                </>
            }
        />
    );
}
